'use client'

import { AttributionCapture } from '@/components/analytics/attribution-capture'
import { PageViewTracker } from '@/components/analytics/page-view-tracker'
import { GlobalAlertsProvider } from '@/components/alert-portal/global-alerts-provider'
import PendingAddressProcessor from '@/components/auth/PendingAddressProcessor'
import { ContentEditProvider } from '@/components/content-edit/content-edit-provider'
import { IsSelectedDebugger } from '@/components/debug/isSelectedDebugger'
import { ClassErrorBoundary } from '@/components/error-boundary'
import { PreferenceSync } from '@/components/preferences/preference-sync'
import UnifiedSocketProvider from '@/components/providers/UnifiedSocketProvider'
// import { SocketStatusIndicator } from '@/components/SocketStatusIndicator'
import { CriticalFieldChangeNotification } from '@/components/appointment/CriticalFieldChangeNotification'
import { CustomSession } from '@/lib/auth/core'
import { NotificationProvider } from '@/lib/services/notifications/use-notifications'
import { WebSocketProvider } from '@/lib/services/socket/WebSocketProvider'
import { useStoreUserSync } from '@/lib/store'
import { StoreProvider } from '@/lib/store/store-provider'
import { I18nProvider, RouterProvider, ToastProvider } from '@heroui/react'
import { SessionProvider } from 'next-auth/react'
import { ThemeProvider as NextThemesProvider } from 'next-themes'
import { useRouter } from 'next/navigation'
import { PropsWithChildren, useCallback, useMemo } from 'react'

// Create store instance outside component to ensure it's stable
function StoreSync() {
  useStoreUserSync()
  return null
}

export function Providers({ children, session }: PropsWithChildren<{ session: CustomSession | null | undefined }>) {
  const router = useRouter()

  // Memoize navigate function to prevent HeroUI context re-renders
  // This is critical for performance as it prevents cascading re-renders
  const navigate = useCallback(
    (href: string) => {
      router.push(href)
    },
    [router]
  )

  // Memoize router configuration for HeroUI v3 (uses RouterProvider + I18nProvider)
  const routerConfig = useMemo(
    () => ({
      navigate
    }),
    [navigate]
  )

  // Analytics: the GTM container (GTM-5Q979BT, with the call-tracking number
  // swap and GA4) is injected at the edge by Cloudflare's Google Tag Gateway on
  // the serving hostname, so the app loads nothing itself. The in-app loader
  // that used to live here booted a second copy of the same container through
  // tagging-server.choice.graphics under the custom dataLayer name 'script',
  // which double-counted pageviews on production and left every in-app push
  // in an array the injected container never read. Funnel and lead pushes go
  // to window.dataLayer (src/lib/analytics/funnel-events.ts).
  //
  // AttributionCapture writes the first-touch cg_attr cookie the lead actions
  // read on the server; PageViewTracker pushes page_view on client-side
  // navigation so the number-swap and click-tracker tags re-run. Both render
  // null (site-tracking-contract.md sections 4 and 5.3).

  // Custom useHref function to handle Next.js basePath if needed
  const makeHref = (href: string) => {
    // You can add basePath handling here if needed
    return href
  }

  return (
    <StoreProvider>
      {/* Static routes (force-static MDX/article/gallery pages) render this layout
          with no request cookies, so server auth() returns null and that null gets
          baked into the cached RSC payload. next-auth's SessionProvider treats a
          provided `session` prop, even null, as the final initial session and skips
          its client-side fetch to /api/auth/session, so a logged-in visitor on one
          of those pages would be stuck looking anonymous for the whole visit.
          Passing undefined instead of null tells SessionProvider it has no session
          yet, so it fetches the real one on mount. */}
      <SessionProvider session={session ?? undefined}>
        {/* No ReduxProvider or PersistGate here: StoreProvider above already
            supplies both, and supplies them correctly. Its PersistGate is
            behind an isClient guard, so the server renders children bare;
            the copy that used to live here had no such guard, so PersistGate
            returned its `loading` value (null) for every server render and
            the entire site shipped an empty <body> to anything that does not
            execute JavaScript. Verified 2026-08-25: every page returned only
            its <title> to curl, Googlebot, GPTBot and ClaudeBot alike. */}
        <StoreSync />
        <AttributionCapture />
        <PageViewTracker />
            {/* There is no theme switcher: Choice Graphics is one light theme,
                site-wide (data-theme="choice-graphics" on <html>). This provider stamps
                the class that Tailwind's `dark:` variant is gated on (@custom-variant in
                globals.css); forced to light, so `dark:` utilities no longer fire and the
                remaining `x dark:y` pairs render their light value, which is the theme's
                intent. Kept rather than deleted so next-themes still owns the html class
                and colour-scheme hint. */}
            <NextThemesProvider attribute='class' forcedTheme='light' disableTransitionOnChange enableSystem={false}>
              <ClassErrorBoundary>
                <RouterProvider {...routerConfig}>
                  <I18nProvider locale='en-US'>
                    {/* One wrap covers both the authenticated and the anonymous branch.
                        It renders no DOM of its own until the server says edit mode is
                        open, which production never does. */}
                    <ContentEditProvider>
                      <NotificationProvider userId={session?.user?.id}>
                        {session?.user?.id ? (
                          // Authenticated users get full socket functionality
                          <UnifiedSocketProvider showConnectionTest={process.env.NODE_ENV === 'development'}>
                            <WebSocketProvider>
                              <IsSelectedDebugger />
                              <PreferenceSync />
                              <PendingAddressProcessor />
                              <GlobalAlertsProvider refreshInterval={60000} />
                              <CriticalFieldChangeNotification />
                              {/* <SocketStatusIndicator /> */}
                              <ToastProvider placement='bottom' data-testid='toast-region' />
                              {children}
                            </WebSocketProvider>
                          </UnifiedSocketProvider>
                        ) : (
                          // Anonymous users get minimal providers (no sockets)
                          <>
                            <IsSelectedDebugger />
                            <GlobalAlertsProvider refreshInterval={60000} />
                            <ToastProvider placement='bottom' data-testid='toast-region' />
                            {children}
                          </>
                        )}
                      </NotificationProvider>
                    </ContentEditProvider>
                  </I18nProvider>
                </RouterProvider>
              </ClassErrorBoundary>
            </NextThemesProvider>
      </SessionProvider>
    </StoreProvider>
  )
}
